/**
 * Set a new password from a reset session: typed twice and checked before it
 * is sent. Resolves `onDone` once Supabase has saved it.
 */

import { useState, type FormEvent } from 'react';
import {
  newPasswordProblem,
  PASSWORD_MIN_LENGTH,
  updatePassword,
} from '@shared/auth/accountSecurity';
import { passwordErrorMessage } from '@shared/auth/passwordErrors';
import {
  AUTH_BODY_CLASS,
  AUTH_ERROR_CLASS,
  AUTH_HEADING_CLASS,
  AUTH_INPUT_CLASS,
  AUTH_LABEL_CLASS,
  AUTH_SUBMIT_CLASS,
  AUTH_SUBMIT_STYLE,
} from './authFormStyles';

export function NewPasswordForm({ onDone }: { onDone: () => void }) {
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const problem = newPasswordProblem(password, confirmation);
    if (problem) {
      setError(problem);
      return;
    }
    setError('');
    setIsSaving(true);
    try {
      await updatePassword({ password });
      onDone();
    } catch (err: unknown) {
      setError(passwordErrorMessage(err));
      setIsSaving(false);
    }
  }

  return (
    <form onSubmit={(e) => void handleSubmit(e)} className="w-full">
      <h2 className={AUTH_HEADING_CLASS}>Set a new password</h2>
      <p className={AUTH_BODY_CLASS}>
        Use at least {PASSWORD_MIN_LENGTH} characters. Your other devices will be signed out.
      </p>

      <div className="mb-4">
        <label htmlFor="new-password" className={AUTH_LABEL_CLASS}>
          New password
        </label>
        <input
          id="new-password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          autoFocus
          autoComplete="new-password"
          className={AUTH_INPUT_CLASS}
        />
      </div>

      <div className="mb-6">
        <label htmlFor="confirm-new-password" className={AUTH_LABEL_CLASS}>
          Confirm new password
        </label>
        <input
          id="confirm-new-password"
          type="password"
          value={confirmation}
          onChange={(e) => setConfirmation(e.target.value)}
          required
          autoComplete="new-password"
          className={AUTH_INPUT_CLASS}
        />
      </div>

      {error && (
        <div role="alert" className={AUTH_ERROR_CLASS}>
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={isSaving}
        className={AUTH_SUBMIT_CLASS}
        style={AUTH_SUBMIT_STYLE}
      >
        {isSaving ? 'Saving...' : 'Set new password'}
      </button>
    </form>
  );
}
